<template>
    <div class="security-fallback-tab">
        <div class="tab-header">
            <h3>{{ $t("voiceagent.securityBackupTabTitle") }}</h3>
            <p>{{ $t("voiceagent.securityBackupTabDescription") }}</p>
        </div>

        <a-form layout="vertical" class="settings-form">
            <!-- Opt Out Sensitive Data Storage -->
            <div class="settings-section">
                <div class="setting-header">
                    <div class="setting-title">
                        <h4>{{ $t("voiceagent.sensitiveDataOptOut") }}</h4>
                        <div class="setting-description">
                            {{ $t("voiceagent.sensitiveDataOptOutDescription") }}
                        </div>
                    </div>
                    <a-switch v-model="formData.opt_out_sensitive_data_storage" @change="updateSettings" />
                </div>
                <div class="setting-content">
                    <div class="description">
                        <a-alert type="info" show-icon>
                            <div slot="message">
                                {{ $t("voiceagent.sensitiveDataOptOutAlert") }}
                            </div>
                        </a-alert>
                    </div>
                </div>
            </div>

            <!-- Fallback Voice ID -->
            <div class="settings-section">
                <div class="setting-header">
                    <h4>{{ $t("voiceagent.fallbackVoiceSection") }}</h4>
                    <div class="setting-description">
                        {{ $t("voiceagent.fallbackVoiceIntro") }}
                    </div>
                </div>

                <div class="setting-content">
                    <div class="description">
                        <p>
                            Seçilen yedek sesler, ana ses servis dışı kaldığında sırayla kullanılır. Yedek sesler ana
                            ses sağlayıcısından farklı olmalıdır.
                        </p>
                    </div>

                    <div
                        class="selected-fallback-voices"
                        v-if="formData.fallback_voice_ids && formData.fallback_voice_ids.length > 0"
                    >
                        <h5>{{ $t("voiceagent.selectedFallbackVoices") }}</h5>
                        <div class="fallback-voice-list">
                            <a-tag
                                v-for="(voiceId, index) in formData.fallback_voice_ids"
                                :key="index"
                                closable
                                @close="removeVoiceFromFallback(index)"
                            >
                                {{ getVoiceName(voiceId) }}
                            </a-tag>
                        </div>
                    </div>

                    <a-button type="dashed" class="add-voice-button" @click="showVoiceSelector">
                        <i class="material-icons">add</i> {{ $t("voiceagent.addFallbackVoice") }}
                    </a-button>

                    <!-- Ses seçici modal -->
                    <a-modal v-model="voiceSelectorVisible" :title="$t('voiceagent.fallbackVoiceModalTitle')" width="800px" :footer="null">
                        <p class="modal-info">
                            {{ $t("voiceagent.fallbackVoiceModalHint") }}
                        </p>
                        <voice-selector @select="addVoiceToFallback" />
                    </a-modal>
                </div>
            </div>

            <!-- Default Dynamic Variables -->
            <div class="settings-section">
                <div class="setting-header">
                    <h4>{{ $t("voiceagent.defaultDynamicVars") }}</h4>
                    <div class="setting-description">
                        {{ $t("voiceagent.defaultDynamicVarsIntro") }}
                    </div>
                </div>

                <div class="setting-content">
                    <div class="description">
                        <p>{{ $t("voiceagent.promptVariablesDefaultValue") }}</p>
                    </div>

                    <div v-if="extractedVariables.length > 0" class="extracted-variables-info">
                        <a-alert type="success" show-icon>
                            <div slot="message">
                                <span>{{ $t("voiceagent.dynamicVarsAlertFound") }}</span>
                                <a-tag v-for="variable in extractedVariables" :key="variable">{{ variable }}</a-tag>
                            </div>
                        </a-alert>
                    </div>

                    <div class="dynamic-variables-list">
                        <div v-for="(value, key, index) in dynamicVariables" :key="index" class="dynamic-variable-item">
                            <div class="variable-wrapper">
                                <div class="variable-input-group">
                                    <a-input
                                        class="variable-key"
                                        addon-before="{{"
                                        addon-after="}}"
                                        :value="key"
                                        @change="(e) => updateVariableKey(key, e.target.value)"
                                        :placeholder="$t('voiceagent.variableKeyPlaceholder')"
                                    />
                                    <a-input
                                        class="variable-value"
                                        v-model="dynamicVariables[key]"
                                        :placeholder="$t('voiceagent.variablePlaceholder')"
                                        @change="updateSettings"
                                    />
                                    <a-button type="danger" icon="delete" @click="removeVariable(key)" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <a-button type="dashed" class="add-variable-button" @click="addVariable">
                        <i class="material-icons">add</i> {{ $t("voiceagent.addVariable") }}
                    </a-button>
                </div>
            </div>

            <!-- Kaydet Butonu -->
            <div class="form-actions">
                <PermissionController :permissions="['voice-agent.update']">
                    <a-button type="primary" @click="saveSettings" :loading="isSaving">
                        {{ $t("voiceagent.saveChangesButton") }}
                    </a-button>
                </PermissionController>
            </div>
        </a-form>
    </div>
</template>

<script>
import _ from "lodash";
import VoiceSelector from "../../../components/VoiceSelector.vue";
import { VOICE_AGENT_API } from "../../../../../../../api/voiceAgent";

export default {
    name: "SecurityFallbackSettingsTab",
    components: {
        VoiceSelector,
    },
    props: {
        agent: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            formData: {
                opt_out_sensitive_data_storage: false,
                fallback_voice_ids: [],
            },
            dynamicVariables: {},
            extractedVariables: [],
            voiceSelectorVisible: false,
            isSaving: false,
            debounceUpdate: null,
            availableVoices: [],
        };
    },
    watch: {
        agent: {
            handler(newValue) {
                if (newValue) {
                    this.initializeFormData();
                    this.loadVoices();
                    this.extractVariablesFromPrompt();
                }
            },
            immediate: true,
            deep: true,
        },
    },
    created() {
        // Debounce fonksiyonu oluşturma
        this.debounceUpdate = _.debounce(this.triggerUpdate, 1000);

        if (this.agent) {
            this.initializeFormData();
            this.loadVoices();
            this.extractVariablesFromPrompt();
        }
    },
    methods: {
        initializeFormData() {
            // Agent'dan verileri formData'ya aktar
            this.formData.opt_out_sensitive_data_storage =
                this.agent.opt_out_sensitive_data_storage !== undefined
                    ? this.agent.opt_out_sensitive_data_storage
                    : false;

            this.formData.fallback_voice_ids = Array.isArray(this.agent.fallback_voice_ids)
                ? [...this.agent.fallback_voice_ids]
                : [];

            // Dinamik değişkenleri yükle
            if (this.agent.llm && this.agent.llm.default_dynamic_variables) {
                this.dynamicVariables = { ...this.agent.llm.default_dynamic_variables };
            } else {
                this.dynamicVariables = {};
            }
        },

        async loadVoices() {
            try {
                if (this.$store && this.$store.dispatch) {
                    const voices = await this.$store.dispatch("voiceAgent/listVoices");
                    this.availableVoices = voices || [];
                }
            } catch (error) {
                console.error("Sesler yüklenirken hata oluştu:", error);
            }
        },

        extractVariablesFromPrompt() {
            if (!this.agent || !this.agent.llm || !this.agent.llm.general_prompt) return;

            const prompt = this.agent.llm.general_prompt;
            const regex = /\{\{([^}]+)\}\}/g;
            const matches = [];
            let match;

            while ((match = regex.exec(prompt)) !== null) {
                matches.push(match[1]);
            }

            this.extractedVariables = [...new Set(matches)]; // Unique değerleri al
        },

        updateSettings() {
            // Değişiklikleri toplu olarak göndermek için debounce kullan
            this.debounceUpdate();
        },

        triggerUpdate() {
            // Değişiklikleri parent komponente bildir
            // console.log("Değişiklikler:", this.formData);
        },

        saveSettings() {
            this.isSaving = true;

            // Agent ayarları için güncelleme verileri
            const agentData = {
                opt_out_sensitive_data_storage: this.formData.opt_out_sensitive_data_storage,
                fallback_voice_ids:
                    this.formData.fallback_voice_ids.length > 0 ? this.formData.fallback_voice_ids : null,
            };

            // Agent verisini güncelle
            this.$emit("update", agentData);

            // LLM dinamik değişkenlerini ayrı bir API çağrısı ile güncelle
            if (this.agent && this.agent.llm && this.agent.response_engine && this.agent.response_engine.llm_id) {
                const llmData = {
                    default_dynamic_variables:
                        this.dynamicVariables && Object.keys(this.dynamicVariables).length > 0
                            ? this.dynamicVariables
                            : null,
                };

                // LLM verilerini ayrı bir API çağrısı ile güncelle (AgentTab.vue örneğini takip ediyoruz)
                VOICE_AGENT_API.llms
                    .update(this.agent.response_engine.llm_id, llmData)
                    .then((response) => {
                        this.$message.success(this.$t("voiceagent.llmDynamicVarsUpdated"));

                        // LLM güncellendiğini parent bileşene bildiriyoruz
                        this.$emit("llm-updated", response);
                    })
                    .catch((error) => {
                        this.$message.error(
                            this.$t("voiceagent.llmUpdateFailed", {
                                msg: error.message || this.$t("voiceagent.unknownError"),
                            })
                        );
                        console.error("LLM güncellenirken hata:", error);
                    });
            }

            setTimeout(() => {
                this.isSaving = false;
            }, 1000);
        },

        // Fallback Voice yönetimi
        showVoiceSelector() {
            this.voiceSelectorVisible = true;
        },

        addVoiceToFallback(voice) {
            // Eğer ana ses ile aynı sağlayıcıdan ses seçilirse uyarı ver
            if (this.agent && this.agent.voice_id) {
                const mainVoiceProvider = this.getVoiceProvider(this.agent.voice_id);

                if (voice.provider === mainVoiceProvider) {
                    this.$message.warning(this.$t("voiceagent.pickDifferentVoiceProvider"));
                    return;
                }
            }

            // Eğer bu ses zaten fallback listesinde varsa ekleme
            if (this.formData.fallback_voice_ids.includes(voice.voice_id)) {
                this.$message.info(this.$t("voiceagent.voiceInFallbackList"));
                return;
            }

            // Yeni sesi ekle
            this.formData.fallback_voice_ids.push(voice.voice_id);
            this.updateSettings();
            this.voiceSelectorVisible = false;
        },

        removeVoiceFromFallback(index) {
            this.formData.fallback_voice_ids.splice(index, 1);
            this.updateSettings();
        },

        getVoiceName(voiceId) {
            const voice = this.availableVoices.find((v) => v.voice_id === voiceId);
            return voice ? voice.voice_name : voiceId;
        },

        getVoiceProvider(voiceId) {
            const voice = this.availableVoices.find((v) => v.voice_id === voiceId);
            return voice ? voice.provider : null;
        },

        // Dinamik değişken yönetimi
        addVariable() {
            const newKey = `new_variable_${Object.keys(this.dynamicVariables).length + 1}`;
            this.$set(this.dynamicVariables, newKey, "");
        },

        updateVariableKey(oldKey, newKey) {
            if (newKey && newKey !== oldKey) {
                const value = this.dynamicVariables[oldKey];

                // Eğer yeni anahtar zaten varsa uyarı ver
                if (this.dynamicVariables.hasOwnProperty(newKey)) {
                    this.$message.warning(this.$t("voiceagent.variableAlreadyExists", { key: newKey }));
                    return;
                }

                // Eski anahtarı sil, yeni anahtarı ekle
                this.$delete(this.dynamicVariables, oldKey);
                this.$set(this.dynamicVariables, newKey, value);
                this.updateSettings();
            }
        },

        removeVariable(key) {
            this.$delete(this.dynamicVariables, key);
            this.updateSettings();
        },
    },
};
</script>

<style scoped lang="scss">
.security-fallback-tab {
    padding: 20px 0;

    .tab-header {
        margin-bottom: 20px;

        h3 {
            margin: 0 0 10px 0;
            font-weight: 500;
        }

        p {
            color: #666;
            margin: 0;
        }
    }

    .settings-form {
        max-width: 800px;
    }

    .settings-section {
        background-color: #f9f9f9;
        padding: 15px;
        border-radius: 4px;
        margin-bottom: 20px;
        border: 1px solid #e8e8e8;

        .setting-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;

            h4 {
                margin: 0 0 5px 0;
                font-weight: 500;
            }

            .setting-title {
                flex: 1;
            }
        }

        .setting-description {
            color: #666;
            font-size: 13px;
            margin-top: 2px;
        }

        .setting-content {
            padding-top: 5px;
        }
    }

    .description {
        color: #666;
        font-size: 12px;
        margin-top: 5px;
        margin-bottom: 15px;
    }

    .selected-fallback-voices {
        margin: 15px 0;

        h5 {
            font-weight: 500;
            margin-bottom: 10px;
        }

        .fallback-voice-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }
    }

    .add-voice-button,
    .add-variable-button {
        width: 100%;
        margin-top: 10px;

        i {
            font-size: 16px;
            margin-right: 5px;
        }
    }

    .modal-info {
        margin-bottom: 15px;
        color: #ff4d4f;
        font-weight: 500;
    }

    .extracted-variables-info {
        margin-bottom: 15px;
    }

    .dynamic-variables-list {
        .dynamic-variable-item {
            margin-bottom: 10px;

            .variable-wrapper {
                display: flex;
                flex-direction: column;

                .variable-input-group {
                    display: flex;
                    gap: 8px;

                    .variable-key {
                        flex: 0 0 40%;
                    }

                    .variable-value {
                        flex: 1;
                    }

                    .ant-btn-danger {
                        margin-left: 5px;
                    }
                }
            }
        }
    }

    .form-actions {
        margin-top: 20px;
        text-align: right;
    }
}
</style>
